import {useMemo} from "react";
import {useSelector} from "react-redux";
import {useQuery} from "react-query";
import type {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import type {User} from "crqlar-react-common/dist/models/identity/user.model";
import {
    Folio,
    FolioStatus,
} from "crqlar-react-common/dist/models/billing/folio.model";
import type {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import type {Reservation} from "crqlar-react-common/dist/models/reservation/reservation.model";
import type {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import type {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {
    Printout,
    PrintoutType,
} from "crqlar-react-common/dist/models/printout/printout.model";
import type {Limit} from "crqlar-react-common/dist/models/common/limit";
import {AgeCategoryService} from "crqlar-react-common/dist/services/ageCategory/ageCategory.service";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import {FolioItemService} from "crqlar-react-common/dist/services/billing/folioItem.service";
import {PrintoutService} from "crqlar-react-common/dist/services/printout/printout.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {printoutHasLanguage, printoutHasLayouts, resolvePrintoutLanguage} from "../../../core/printoutDocument";
import type {PrintoutVariableResolveContext} from "../../../variables/printoutVariableResolver";

type ResolveRecord = Record<string, unknown>;

interface FolioPrintRelatedData {
    ageCategories: AgeCategory[];
    folioItems: FolioItem[];
    profile?: Profile;
    reservation?: Reservation;
    resources: Resource[];
    sector?: Sector;
}

export interface FolioPrintData {
    ageCategories: AgeCategory[];
    context?: PrintoutVariableResolveContext;
    errorMessage?: string;
    folio?: Folio;
    folioItems: FolioItem[];
    isLoading: boolean;
    language?: string;
    profile?: Profile;
    reservation?: Reservation;
    resources: Resource[];
    sector?: Sector;
    selectedPrintout?: Printout;
    printouts: Printout[];
}

const LIST_LIMIT: Limit = {
    offset: 0,
    limit: 1000,
    sort: [{field: "id", direction: "ASC"}],
};

const PRINTOUT_LIST_LIMIT: Limit = {
    offset: 0,
    limit: 1000,
    sort: [{field: "name", direction: "ASC"}],
};

const getErrorMessage = (error: unknown) => {
    if (!error) return undefined;
    if (error instanceof Error) return error.message;
    return "Unable to load folio print preview data.";
};

const uniqueIds = (ids: Array<string | undefined>) => Array.from(new Set(
    ids.filter((id): id is string => Boolean(id)),
));

// Fetch both resourceIds and roomID values so unit names never fall back to raw ids.
const getReservationResourceIds = (reservation?: Reservation) => uniqueIds([
    ...(reservation?.resourceIds ?? []),
    ...(reservation?.resourceAdditionalInfo ?? []).map(info => info.roomID),
]);

const getReservationScopeData = async (reservation?: Reservation) => {
    if (!reservation) {
        return {
            resources: [] as Resource[],
            sector: undefined,
        };
    }

    const resourceIds = getReservationResourceIds(reservation);
    const [resources, sector] = await Promise.all([
        resourceIds.length
            ? ResourceService.getAllResources({
                id: resourceIds,
                limit: LIST_LIMIT,
            }).then(page => page.content ?? []).catch(() => [])
            : Promise.resolve([] as Resource[]),
        reservation.sectorId
            ? SectorService.getSectorById(reservation.sectorId).catch(() => undefined)
            : Promise.resolve(undefined),
    ]);

    return {
        resources,
        sector,
    };
};

const getRelatedFolioPrintData = async (folio: Folio): Promise<FolioPrintRelatedData> => {
    const [ageCategories, folioItemsPage, profile, reservation] = await Promise.all([
        // Guest-count keys require age-category records for correct labels.
        AgeCategoryService.getAllAgeCategories({
            limit: LIST_LIMIT,
        }).then(page => page.content ?? []).catch(() => [] as AgeCategory[]),
        FolioItemService.getAllFolioItems({
            folioIds: folio.id ? [folio.id] : [],
            limit: LIST_LIMIT,
        }),
        folio.profileId
            ? ProfileService.getProfileById(folio.profileId).catch(() => undefined)
            : Promise.resolve(undefined),
        folio.reservationId
            ? ReservationService.getReservationById(folio.reservationId).catch(() => undefined)
            : Promise.resolve(undefined),
    ]);
    const {resources, sector} = await getReservationScopeData(reservation);

    return {
        ageCategories,
        folioItems: folioItemsPage.content ?? [],
        profile,
        reservation,
        resources,
        sector,
    };
};

const isFolioPrintoutType = (printout: Printout) => (
    printout.type === PrintoutType.FOLIO_CONFIRMATION && printoutHasLayouts(printout)
);

const selectFolioPrintout = (
    printouts: Printout[],
    selectedPrintoutId?: string,
    profile?: Profile,
) => (
    printouts.find(printout => printout.id === selectedPrintoutId)
    ?? [...printouts].sort((left, right) => {
        const rightMatches = printoutHasLanguage(right, profile?.languageCode) ? 1 : 0;
        const leftMatches = printoutHasLanguage(left, profile?.languageCode) ? 1 : 0;
        return rightMatches - leftMatches || (left.name ?? "").localeCompare(right.name ?? "");
    })[0]
);

export const useFolioPrintData = (
    folioId?: string,
    selectedPrintoutId?: string,
    requestedLanguage?: string,
): FolioPrintData => {
    const organization = useOrganization();
    const user = useSelector<any, User | undefined>(state => state.auth.user);

    const folioQuery = useQuery(
        ["printV3", "folio", folioId],
        () => FolioService.getById(folioId!),
        {
            enabled: Boolean(folioId),
            refetchOnWindowFocus: false,
        },
    );

    const relatedDataQuery = useQuery(
        ["printV3", "folioRelatedData", folioQuery.data?.id],
        () => getRelatedFolioPrintData(folioQuery.data!),
        {
            enabled: Boolean(folioQuery.data?.id),
            refetchOnWindowFocus: false,
        },
    );

    const totalQuery = useQuery(
        ["printV3", "folioTotal", folioQuery.data?.id, folioQuery.data?.status],
        () => FolioService.calculateTotal({id: folioQuery.data!.id!}),
        {
            enabled: Boolean(folioQuery.data?.id && folioQuery.data.status === FolioStatus.DRAFT),
            refetchOnWindowFocus: false,
        },
    );

    const printoutQuery = useQuery(
        ["printV3", "folioPrintouts"],
        () => PrintoutService.getAllPrintouts({
            archived: false,
            limit: PRINTOUT_LIST_LIMIT,
        }).then(page => (
            page.content?.filter(isFolioPrintoutType) ?? []
        )),
        {refetchOnWindowFocus: false},
    );

    const templateQuery = useQuery(
        ["printV3", "printoutTemplates"],
        () => PrintoutService.getPrintoutTemplates(),
        {refetchOnWindowFocus: false},
    );

    /*
        The backend's own draft for the built-in statement, carrying every language the template
        ships - the document shown is still picked per guest language further down.
    */
    const fallbackPrintout = useMemo(() => {
        const draft = templateQuery.data?.find(entry => entry.id === "folio-statement")?.draft;
        if (!draft) return undefined;

        return {...draft, id: "built-in-folio-statement"};
    }, [templateQuery.data]);

    const printouts = useMemo(
        () => printoutQuery.data?.length
            ? printoutQuery.data
            : fallbackPrintout ? [fallbackPrintout] : [],
        [fallbackPrintout, printoutQuery.data],
    );

    const selectedPrintout = useMemo(
        () => selectFolioPrintout(
            printouts,
            selectedPrintoutId,
            relatedDataQuery.data?.profile,
        ),
        [
            relatedDataQuery.data?.profile,
            selectedPrintoutId,
            printouts,
        ],
    );

    const folio = useMemo(() => {
        if (!folioQuery.data) return undefined;

        return {
            ...folioQuery.data,
            total: totalQuery.data ?? folioQuery.data.total,
        };
    }, [
        folioQuery.data,
        totalQuery.data,
    ]);

    const language = selectedPrintout
        ? resolvePrintoutLanguage(
            selectedPrintout,
            requestedLanguage ?? relatedDataQuery.data?.profile?.languageCode,
        )
        : undefined;

    const context = useMemo<PrintoutVariableResolveContext | undefined>(() => {
        if (!folio || !selectedPrintout || !language) return undefined;

        const relatedData = relatedDataQuery.data;

        return {
            ageCategories: relatedData?.ageCategories as unknown as ResolveRecord[] | undefined,
            folio: folio as unknown as ResolveRecord,
            folioItems: (relatedData?.folioItems ?? []) as unknown as ResolveRecord[],
            language,
            now: new Date(),
            organization: organization as unknown as ResolveRecord | undefined,
            profile: relatedData?.profile as unknown as ResolveRecord | undefined,
            reservation: relatedData?.reservation as unknown as ResolveRecord | undefined,
            resources: (relatedData?.resources ?? []) as unknown as ResolveRecord[],
            sector: relatedData?.sector as unknown as ResolveRecord | undefined,
            user: user as unknown as ResolveRecord | undefined,
        };
    }, [
        folio,
        language,
        organization,
        relatedDataQuery.data,
        selectedPrintout,
        user,
    ]);

    const errorMessage = getErrorMessage(folioQuery.error)
        ?? getErrorMessage(relatedDataQuery.error)
        ?? getErrorMessage(totalQuery.error)
        ?? getErrorMessage(printoutQuery.error)
        ?? getErrorMessage(templateQuery.error);

    return {
        ageCategories: relatedDataQuery.data?.ageCategories ?? [],
        context,
        errorMessage,
        folio,
        folioItems: relatedDataQuery.data?.folioItems ?? [],
        isLoading: folioQuery.isLoading
            || relatedDataQuery.isLoading
            || totalQuery.isLoading
            || printoutQuery.isLoading
            || templateQuery.isLoading,
        language,
        profile: relatedDataQuery.data?.profile,
        reservation: relatedDataQuery.data?.reservation,
        resources: relatedDataQuery.data?.resources ?? [],
        sector: relatedDataQuery.data?.sector,
        selectedPrintout,
        printouts,
    };
};
